import { isLegacyBrokenSafeUser } from '@/constants/legacy-broken-safes'
import { RELAYER_FALLBACK_URL, RELAYER_URL } from '@/constants/polymarket'
import {
	createPolymarketClobClient,
	getOrCreatePolymarketApiCredentials,
	savePolymarketTradingSession,
} from '@/lib/polymarket/clob-session'
import {
	fetchOpenPositionCount,
	persistActiveWalletPreference,
	pickDefaultActiveWallet,
	readCombinedCashRaw,
} from '@/lib/polymarket/wallet-preference'
import {
	PolymarketSafeUnavailableError,
	ensurePolymarketWalletReady,
	wrapStrandedUsdceIfNeeded,
} from '@/lib/polymarket/wallet-readiness'
import { isRateLimitError } from '@/lib/relayer-fallback'
import {
	deriveDepositWalletAddress,
	syncDepositWalletClobBalances,
} from '@/lib/wallets/polymarket-deposit'
import { useWallet } from '@/providers/WalletContext'
import type { PolymarketActiveWallet } from '@/utils/polymarket-wallet-address'
import { type TradingSession, loadSession } from '@/utils/session'
import type { RelayClient } from '@polymarket/builder-relayer-client'
import type { ClobClient } from '@polymarket/clob-client-v2'
import { useCallback, useEffect, useRef, useState } from 'react'
import type { Address } from 'viem'

export type TradingStatus = 'disconnected' | 'initializing' | 'ready' | 'error'

export interface PolymarketTradingState {
	status: TradingStatus
	currentAction: string | null
	error: Error | null
	clobClient: ClobClient | null
	relayClient: RelayClient | null
	/**
	 * Compatibility name retained for this pass. Underlying value is the
	 * Polymarket V1-derived Safe; consumers should prefer the upcoming
	 * provider-owned market wallet hook for display purposes.
	 */
	safeAddress: string | undefined
	funderAddress: string | undefined
	eoaAddress: string | undefined
	retry: () => void
}

export default function usePolymarketTrading(): PolymarketTradingState {
	const [status, setStatus] = useState<TradingStatus>('disconnected')
	const [currentAction, setCurrentAction] = useState<string | null>(null)
	const [error, setError] = useState<Error | null>(null)
	const [clobClient, setClobClient] = useState<ClobClient | null>(null)
	const [relayClient, setRelayClient] = useState<RelayClient | null>(null)
	const [_session, setSession] = useState<TradingSession | null>(null)
	const [funderAddress, setFunderAddress] = useState<`0x${string}` | undefined>(undefined)
	const [walletKind, setWalletKind] = useState<'safe' | 'deposit' | null>(null)

	const isInitializingRef = useRef(false)

	const {
		eoaAddress,
		walletClient,
		ethersSigner,
		publicClient,
		polymarketSafe,
		isPolymarketSafeResolved,
		polymarketActiveWallet,
		setPolymarketActiveWallet,
		userId,
	} = useWallet()
	// Retained for backward compat: only populated when the resolved funder is a Safe.
	const safeAddress = walletKind === 'safe' ? funderAddress : undefined

	const retry = useCallback(() => {
		setError(null)
		setStatus('disconnected') // This triggers the auto-init effect
	}, [])

	const initialize = useCallback(async () => {
		if (!(eoaAddress && walletClient && ethersSigner)) {
			return
		}

		if (isInitializingRef.current) {
			return
		}
		isInitializingRef.current = true

		setStatus('initializing')
		setError(null)
		setFunderAddress(undefined)
		setWalletKind(null)

		try {
			// Effective wallet-switcher preference for THIS init run. Starts from
			// the stored preference; may be filled in by auto-pick (first login
			// after the switcher release) or flipped to 'deposit' by the
			// Safe-unavailable auto-revert below.
			let effectiveActiveWallet: PolymarketActiveWallet | null = polymarketActiveWallet

			const persistPreference = (value: PolymarketActiveWallet) => {
				setPolymarketActiveWallet(value)
				persistActiveWalletPreference(value).catch(err =>
					console.warn('[trading] failed to persist wallet preference:', err)
				)
			}

			// One-time auto-pick for legacy Safe users without a stored
			// preference: choose the wallet holding more cash, but never pull
			// the user away from a Safe with open positions, and never pick an
			// undeployed Safe (post-cutover deploys are rejected by Polymarket).
			if (polymarketSafe && !effectiveActiveWallet && publicClient) {
				setCurrentAction('Checking wallets...')
				try {
					const safeAddr = polymarketSafe as Address
					const depositWallet = deriveDepositWalletAddress(eoaAddress as Address)
					const [code, safeCashRaw, depositCashRaw, openPositions] = await Promise.all([
						publicClient.getCode({ address: safeAddr }),
						readCombinedCashRaw(publicClient, safeAddr),
						readCombinedCashRaw(publicClient, depositWallet),
						fetchOpenPositionCount(safeAddr),
					])
					effectiveActiveWallet = pickDefaultActiveWallet({
						safeDeployed: !!code && code !== '0x',
						safeHasOpenPositions: openPositions > 0,
						safeCashRaw,
						depositCashRaw,
					})
					persistPreference(effectiveActiveWallet)
				} catch (err) {
					// Fail-open to legacy default (Safe path); retry next login.
					console.warn('[trading] wallet auto-pick failed:', err)
					effectiveActiveWallet = null
				}
			}

			const initWalletReady = async (relayerUrl: string) => {
				return ensurePolymarketWalletReady({
					polymarketSafe,
					eoaAddress,
					walletClient,
					publicClient,
					relayerUrl,
					onProgress: msg => setCurrentAction(msg),
					// Broken-Safe users have a deployed legacy Safe with funds — the
					// new orphan-Safe-with-funds heuristic would otherwise re-claim
					// them for the (broken) Safe path. The upgrade modal handles
					// draining; trading must always use the deposit wallet.
					forceDepositWallet: isLegacyBrokenSafeUser(userId),
					activeWallet: effectiveActiveWallet,
				})
			}

			// Auto-revert: a preference-initiated Safe path that turns out to be
			// unusable (Safe not deployed — Polymarket rejects fresh deploys)
			// must not strand the user in a dead session. Flip the preference
			// back to 'deposit' and re-init on the deposit-wallet path.
			const initWithAutoRevert = async (relayerUrl: string) => {
				try {
					return await initWalletReady(relayerUrl)
				} catch (err) {
					if (err instanceof PolymarketSafeUnavailableError) {
						console.warn('[trading] Safe path unavailable, reverting to deposit wallet:', err)
						effectiveActiveWallet = 'deposit'
						persistPreference('deposit')
						setCurrentAction('Legacy wallet unavailable — using deposit wallet...')
						return initWalletReady(relayerUrl)
					}
					throw err
				}
			}

			let funderAddress: `0x${string}`
			let relay: RelayClient | null
			let resolvedWalletKind: 'safe' | 'deposit'

			try {
				;({
					funderAddress,
					relayClient: relay,
					walletKind: resolvedWalletKind,
				} = await initWithAutoRevert(RELAYER_URL))
			} catch (relayErr) {
				if (isRateLimitError(relayErr)) {
					console.warn('Relayer returned 429, switching to fallback:', RELAYER_FALLBACK_URL)
					setCurrentAction('Switching relayer...')
					;({
						funderAddress,
						relayClient: relay,
						walletKind: resolvedWalletKind,
					} = await initWithAutoRevert(RELAYER_FALLBACK_URL))
				} else {
					throw relayErr
				}
			}
			setRelayClient(relay)

			setCurrentAction('Getting credentials...')
			const apiCreds = await getOrCreatePolymarketApiCredentials({ eoaAddress, ethersSigner })

			const clobClient = createPolymarketClobClient({
				polymarketSafe,
				funderAddress,
				walletKind: resolvedWalletKind,
				eoaAddress,
				ethersSigner,
				walletClient,
				apiCreds,
			})
			setClobClient(clobClient)
			setFunderAddress(funderAddress)
			setWalletKind(resolvedWalletKind)

			// Sync CLOB balance/allowance snapshot for deposit-wallet users so the
			// CLOB sees the on-chain state before the first order.
			if (resolvedWalletKind === 'deposit') {
				await syncDepositWalletClobBalances({ clob: clobClient }).catch(err =>
					console.warn('[trading] syncDepositWalletClobBalances failed:', err)
				)
			}

			savePolymarketTradingSession({
				eoaAddress,
				funderAddress,
				apiCredentials: apiCreds,
			})
			setSession(loadSession(eoaAddress))

			setCurrentAction(null)
			setStatus('ready')
		} catch (err) {
			console.error('Trading initialization error:', err)
			setError(err instanceof Error ? err : new Error('Failed to initialize trading'))
			setCurrentAction(null)
			setFunderAddress(undefined)
			setWalletKind(null)
			setStatus('error')
		} finally {
			isInitializingRef.current = false
		}
	}, [
		eoaAddress,
		walletClient,
		ethersSigner,
		polymarketSafe,
		polymarketActiveWallet,
		setPolymarketActiveWallet,
		publicClient,
		userId,
	])

	// Auto-initialize when wallet connects
	useEffect(() => {
		if (!eoaAddress) {
			setStatus('disconnected')
			setClobClient(null)
			setRelayClient(null)
			setSession(null)
			setFunderAddress(undefined)
			setWalletKind(null)
			setCurrentAction(null)
			setError(null)
			return
		}

		const stored = loadSession(eoaAddress)
		if (stored) {
			setSession(stored)
		}

		// Hold initialization until the user's polymarketSafe value has been
		// resolved by the profile fetch. Without this gate, init runs with the
		// initial `polymarketSafe=null` from useState and routes legacy Safe
		// users (with users.polymarketSafe populated in DB) onto the deposit-
		// wallet path — Portfolio then queries the wrong address and shows an
		// empty balance until the user hard-refreshes.
		if (status === 'disconnected' && isPolymarketSafeResolved) {
			initialize()
		}
	}, [eoaAddress, status, initialize, isPolymarketSafeResolved])

	// Auto-wrap stranded USDC.e in the background (Safe users only).
	// Polls every 30s; locked by a ref so concurrent polls don't double-fire wrap txs.
	// Deposit-wallet users have wrapping handled by ensureDepositWalletReady at init time.
	const isAutoWrappingRef = useRef(false)
	useEffect(() => {
		if (
			status !== 'ready' ||
			walletKind !== 'safe' ||
			!(safeAddress && relayClient && publicClient)
		) {
			return
		}

		const checkAndWrap = async () => {
			if (isAutoWrappingRef.current) return
			isAutoWrappingRef.current = true
			try {
				await wrapStrandedUsdceIfNeeded({
					relayClient,
					publicClient,
					funderAddress: safeAddress,
					onlyWhenPusdEmpty: false,
				})
			} catch (err) {
				console.warn('[auto-wrap] failed:', err)
			} finally {
				isAutoWrappingRef.current = false
			}
		}

		checkAndWrap()
		const intervalId = setInterval(checkAndWrap, 30_000)
		return () => clearInterval(intervalId)
	}, [status, walletKind, safeAddress, relayClient, publicClient])

	return {
		status,
		currentAction,
		error,
		clobClient,
		relayClient,
		safeAddress,
		funderAddress,
		eoaAddress,
		retry,
	}
}
